import type { DebriefSharePayload } from './types'

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderInline(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
}

function formatFullDate(ts: number): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(ts))
}

function renderMarkdown(text: string): string {
  const lines = text.split('\n')
  const out: string[] = []
  let inList = false

  for (const raw of lines) {
    const line = raw.trimEnd()

    const hMatch = line.match(/^(#{1,3})\s+(.+)/)
    if (hMatch) {
      if (inList) { out.push('</ul>'); inList = false }
      const level = Math.min(hMatch[1]!.length + 2, 6)
      out.push(`<h${level}>${renderInline(hMatch[2]!)}</h${level}>`)
      continue
    }

    const bulletMatch = line.match(/^[-*](?!\*)\s+(.+)/)
    if (bulletMatch) {
      if (!inList) { out.push('<ul>'); inList = true }
      out.push(`<li>${renderInline(bulletMatch[1]!)}</li>`)
      continue
    }

    if (inList) { out.push('</ul>'); inList = false }

    if (line === '') {
      out.push('<br/>')
    } else {
      out.push(`<p>${renderInline(line)}</p>`)
    }
  }

  if (inList) out.push('</ul>')
  return out.join('')
}

/** Returns an HTML body fragment (no <html>/<body> wrapper — index.html provides the shell). */
export function renderDebriefHtml(payload: DebriefSharePayload): string {
  const { data, sections } = payload
  const blocks: string[] = []

  blocks.push(`
    <header class="hdr">
      <h1>${escapeHtml(payload.meetingTitle)}</h1>
      <p class="meta">
        ${payload.meetingDate ? escapeHtml(formatFullDate(payload.meetingDate)) : ''}
        ${
          payload.participantCount > 0
            ? `${payload.meetingDate ? ' · ' : ''}${payload.participantCount} participant${payload.participantCount === 1 ? '' : 's'}`
            : ''
        }
      </p>
    </header>
  `)

  if (sections.decisions && data.decisions && data.decisions.length > 0) {
    blocks.push(`
      <section><h2>Decisions</h2><ul>${data.decisions
        .map(
          (d) =>
            `<li><div>${escapeHtml(d.text)}</div>${d.explanation ? `<div class="explanation">${escapeHtml(d.explanation)}</div>` : ''}</li>`,
        )
        .join('')}</ul></section>
    `)
  }

  if (sections.actionItems && data.actionItems && data.actionItems.length > 0) {
    blocks.push(`
      <section><h2>Action Items</h2><ul>${data.actionItems
        .map(
          (a) =>
            `<li><div>${escapeHtml(a.task)}${a.owner ? ` <span class="owner">@${escapeHtml(a.owner)}</span>` : ''}</div>${a.explanation ? `<div class="explanation">${escapeHtml(a.explanation)}</div>` : ''}</li>`,
        )
        .join('')}</ul></section>
    `)
  }

  if (sections.summary && data.summary) {
    blocks.push(`
      <section><h2>Summary</h2><div class="summary">${renderMarkdown(data.summary)}</div></section>
    `)
  }

  if (sections.factChecks && data.factChecks && data.factChecks.length > 0) {
    blocks.push(`
      <section><h2>Fact Checks</h2><ul>${data.factChecks
        .map(
          (f) =>
            `<li><div>${escapeHtml(f.claimed)} <span class="arrow">→</span> ${escapeHtml(f.correction)}</div>${f.evidenceQuote ? `<div class="explanation">${escapeHtml(f.evidenceQuote)}</div>` : ''}</li>`,
        )
        .join('')}</ul></section>
    `)
  }

  if (sections.suggestedQuestions && data.suggestedQuestions && data.suggestedQuestions.length > 0) {
    blocks.push(`
      <section><h2>Suggested Questions</h2><ul>${data.suggestedQuestions
        .map(
          (q) =>
            `<li><div>${escapeHtml(q.question)}</div>${q.explanation ? `<div class="explanation">${escapeHtml(q.explanation)}</div>` : ''}</li>`,
        )
        .join('')}</ul></section>
    `)
  }

  return blocks.join('')
}
